import React, {
  FC,
  useState,
  createContext,
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
  useContext,
} from 'react';

import { globalHistory } from '@reach/router';

import { TabContext } from '@mui/lab';
import { withStyles } from '@material-ui/core/styles';
import { Header } from 'components/header';
import { LeftDrawer } from 'components/drawer';
import LinearProgress from '@mui/material/LinearProgress';
import { ContainerStyled, Wrapper, SlidingContent } from './styled';
import { TabBar } from './TabBar';

import { AuthDisplayState } from 'components/authorizationPopups';

type NetworkProgressActions = { show: () => void; hide: () => void };
const NetworkProgressContext = createContext<NetworkProgressActions>({
  show: () => void 0,
  hide: () => void 0,
});

// Supports a provider that allows the setting of below appbar tabs (see userdetails for an example)
// The default needs to support item access so cannot be udefined, and needs to match type
export const VansanityTabContext = createContext<
  Dispatch<SetStateAction<Array<string>>>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>((_: Array<string>) => {}); // eslint-disable-line @typescript-eslint/no-empty-function

type Props = {
  authModalDisplay: AuthDisplayState;
  setAuthModalDisplay: Dispatch<AuthDisplayState>;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const Layout: FC<Props> = ({
  authModalDisplay,
  setAuthModalDisplay,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [tabTitles, setTabTitles] = useState<Array<string>>([]);
  const [progressVisible, setProgressVisible] = useState<boolean>(false);

  // Listen for route changes and reset our below appbar tabs on them since
  // a new page is not oging to want tabs from prior page.
  // https://github.com/reach/router/issues/262
  useEffect(() => {
    const unsubscriber = globalHistory.listen(({ action }) => {
      if (action === 'PUSH') {
        setTabTitles([]);
      }
    });
    return () => unsubscriber();
  }, []);

  const networkProgressActions: NetworkProgressActions = useMemo(
    () => ({
      show: () => setProgressVisible(true),
      hide: () => setProgressVisible(false),
    }),
    [],
  );

  return (
    <Wrapper>
      <LeftDrawer isOpen={open} setIsOpen={setOpen} />
      <SlidingContent>
        <Header
          drawerOpen={open}
          toggleDrawer={() => setOpen(!open)}
          setAuthModalDisplay={setAuthModalDisplay}
          authModalDisplay={authModalDisplay}
        />
        <LinearProgress
          sx={{
            width: '100%',
            height: '8px',
            marginTop: '45px',
            position: 'fixed',
            zIndex: 10,
            opacity: Number(progressVisible),
          }}
          color="secondary"
        />
        {tabTitles.length != 0 && (
          <TabBar
            value={tabValue}
            valueSetter={setTabValue}
            titles={tabTitles}
          />
        )}
        <TabContext value={tabValue.toString()}>
          <VansanityTabContext.Provider value={setTabTitles}>
            <NetworkProgressContext.Provider value={networkProgressActions}>
              <ContainerStyled id="layout-main">{children}</ContainerStyled>
            </NetworkProgressContext.Provider>
          </VansanityTabContext.Provider>
        </TabContext>
      </SlidingContent>
    </Wrapper>
  );
};

export const useNetworkActivityIndicator = (loading: boolean): void => {
  const { show, hide } = useContext(NetworkProgressContext);

  // useEffect is always called after the component renders
  // so we need to wrap dispatch in it to ensure that the progress bar doesn't
  // try to rerender until the sub component using this hook has finished rendering.

  // easiest to just always use this hook, that way we can't mess this again up later.

  useEffect(() => {
    (loading ? show : hide)();
  }, [loading, show, hide]);
};
